import {
	VictoryArea,
	VictoryChart,
	VictoryGroup,
	VictoryLabel,
	VictoryPolarAxis,
	VictoryTheme,
} from "victory-native";
import { useAppTheme } from "../../../useAppTheme";
import { CHART_DIMENSIONS, getChartFontFamily } from "../constants";
import { useChartFontScale } from "../hooks/useChartFontScale";
import type { RadarChartData } from "../types";

type SkillRadarChartProps = {
	data: RadarChartData;
	appFont?: string;
	containerWidth: number;
};

export const SkillRadarChart = ({
	data,
	appFont,
	containerWidth,
}: SkillRadarChartProps) => {
	const theme = useAppTheme();
	const config = CHART_DIMENSIONS.radarChart;
	const fontScale = useChartFontScale(containerWidth, { minScale: 0.65 });

	const chartWidth = containerWidth;
	const chartHeight = Math.round(containerWidth * 0.8);

	const longestSkillLabelLength = Math.max(
		0,
		...data.map((item) => item.skill.length),
	);
	const horizontalPaddingBoost = Math.min(
		Math.max(longestSkillLabelLength * 1.1, 0),
		48,
	);

	const responsivePadding = {
		top: Math.min(Math.round(chartHeight * 0.08), 40),
		bottom: Math.min(Math.round(chartHeight * 0.08), 40),
		left: Math.min(Math.round(chartWidth * 0.16 + horizontalPaddingBoost), 108),
		right: Math.min(
			Math.round(chartWidth * 0.16 + horizontalPaddingBoost),
			108,
		),
	};

	const chartData = data.map((item, index) => ({
		x: index + 1,
		y: item.value,
	}));

	const targetData = data.flatMap((item, index) =>
		item.target !== undefined ? [{ x: index + 1, y: item.target }] : [],
	);

	const peerAvgData = data.flatMap((item, index) =>
		item.peerAvg !== undefined ? [{ x: index + 1, y: item.peerAvg }] : [],
	);

	const getTextAnchor = (index: number): "start" | "middle" | "end" => {
		const anglePerAxis = 360 / data.length;
		const angle = 90 - index * anglePerAxis;
		const normalizedAngle = ((angle % 360) + 360) % 360;

		if (normalizedAngle >= 45 && normalizedAngle <= 135) {
			return "middle";
		}
		if (normalizedAngle >= 315 || normalizedAngle <= 45) {
			return "start";
		}
		if (normalizedAngle >= 135 && normalizedAngle <= 225) {
			return "end";
		}
		return "middle";
	};

	const getAxisLabelPadding = (index: number) => {
		const anglePerAxis = 360 / data.length;
		const angle = 90 - index * anglePerAxis;
		const normalizedAngle = ((angle % 360) + 360) % 360;
		const isTopOrBottom =
			(normalizedAngle >= 45 && normalizedAngle <= 135) ||
			(normalizedAngle >= 225 && normalizedAngle <= 315);

		return isTopOrBottom ? 18 * fontScale : 10 * fontScale;
	};

	const peerAvgColor =
		(theme.colors as { peerAvg?: string; secondary?: string }).peerAvg ??
		(theme.colors as { secondary?: string }).secondary ??
		"#4b6cb7";
	const userColor = theme.colors.primary;
	const resolvedPeerAvgColor =
		peerAvgColor === userColor ? "#4b6cb7" : peerAvgColor;

	const maxCharsPerLine = containerWidth < 380 ? 10 : 14;
	const maxLines = 2;
	const formatSkillLabel = (label: string) => {
		const words = label.split(/\s+/).filter(Boolean);
		const lines: string[] = [];
		let currentLine = "";

		for (const word of words) {
			const nextLine = currentLine ? `${currentLine} ${word}` : word;
			if (nextLine.length <= maxCharsPerLine) {
				currentLine = nextLine;
				continue;
			}
			if (currentLine) {
				lines.push(currentLine);
			}

			if (word.length > maxCharsPerLine) {
				lines.push(`${word.slice(0, maxCharsPerLine - 1)}…`);
				currentLine = "";
			} else {
				currentLine = word;
			}

			if (lines.length === maxLines) {
				break;
			}
		}

		if (lines.length < maxLines && currentLine) {
			lines.push(currentLine);
		}

		const clipped = lines.slice(0, maxLines);
		if (
			clipped.length === maxLines &&
			words.join(" ").length > clipped.join(" ").length
		) {
			const lastLine = clipped[maxLines - 1] ?? "";
			clipped[maxLines - 1] = lastLine.endsWith("…")
				? lastLine
				: `${lastLine.slice(0, Math.max(lastLine.length - 1, 0))}…`;
		}

		return clipped.join("\n");
	};

	return (
		<VictoryChart
			polar
			theme={VictoryTheme.material}
			domain={{ y: [0, 100] }}
			padding={responsivePadding}
			startAngle={90}
			endAngle={-270}
			width={chartWidth}
			height={chartHeight}
		>
			<VictoryGroup>
				<VictoryArea
					data={chartData}
					style={{
						data: {
							fill: theme.colors.primaryContainer,
							stroke: userColor,
							strokeWidth: 2,
						},
					}}
				/>

				{targetData.length > 0 && (
					<VictoryArea
						data={targetData}
						style={{
							data: {
								fill: "none",
								stroke: theme.colors.success.main,
								strokeWidth: 2,
								strokeDasharray: "4,4",
							},
						}}
					/>
				)}

				{peerAvgData.length > 0 && (
					<VictoryArea
						data={peerAvgData}
						style={{
							data: {
								fill: "none",
								stroke: resolvedPeerAvgColor,
								strokeWidth: 2,
								strokeDasharray: "4,4",
							},
						}}
					/>
				)}
			</VictoryGroup>

			<VictoryPolarAxis
				labelPlacement="vertical"
				dependentAxis
				style={{
					axis: { stroke: "none" },
					grid: {
						stroke: theme.colors.outlineVariant,
						strokeWidth: 1,
						strokeDasharray: "5,5",
					},
					tickLabels: {
						fill: theme.colors.onSurfaceVariant,
						fontSize: config.tickLabelFontSize * fontScale,
						fontFamily: getChartFontFamily(appFont),
						fontWeight: "400",
						angle: 0,
					},
				}}
				tickValues={[0, 25, 50, 75, 100]}
				tickLabelComponent={<VictoryLabel dx={10} />}
			/>

			<VictoryPolarAxis
				style={{
					axis: { stroke: "none" },
					grid: {
						stroke: theme.colors.outlineVariant,
						strokeWidth: 1,
					},
					tickLabels: {
						fill: theme.colors.onSurface,
						fontSize: config.axisLabelFontSize * fontScale,
						fontFamily: getChartFontFamily(appFont),
						fontWeight: "400",
						padding: (datum: { index?: string | number }) => {
							const index =
								typeof datum.index === "number"
									? datum.index
									: Number.parseInt(String(datum.index ?? 0), 10);
							return getAxisLabelPadding(index);
						},
						angle: 0,
						textAnchor: (datum: { index?: string | number }) => {
							const index =
								typeof datum.index === "number"
									? datum.index
									: Number.parseInt(String(datum.index ?? 0), 10);
							return getTextAnchor(index);
						},
					},
				}}
				tickValues={data.map((_, i) => i + 1)}
				tickFormat={data.map((d) => formatSkillLabel(d.skill))}
				tickLabelComponent={<VictoryLabel lineHeight={1.2} />}
			/>
		</VictoryChart>
	);
};
